import React, { FunctionComponent, useState, useRef, useEffect } from 'react';
import styled, { css } from 'styled-components';
import Arrow from 'public/images/arrow.svg';

interface IAccordion {
  label: string;
  color?: string;
  style?: any;
  iconColor?: string;
  buttonColor?: string;
  index: number | string;
  setIndex: (i: number | string) => void;
  direction: string;
  justify: string;
  breaker: boolean;
  disabled: boolean;
  header: boolean;
  i: number | string;
  arrow?: boolean;
  children?: React.ReactNode;
}

export const Accordion: FunctionComponent<IAccordion> = ({
  label,
  color,
  index,
  iconColor = '#F7F7F0',
  buttonColor,
  style,
  setIndex,
  direction,
  justify,
  breaker,
  disabled,
  header,
  i,
  children,
  arrow,
}) => {
  const ref = useRef<HTMLInputElement>(null);

  const open = i === index;

  const [height, setHeight] = useState(0);

  const onClick = () => {
    setIndex(i === index ? -1 : i);
    if (!disabled) setHeight(ref!.current!.scrollHeight);
  };

  useEffect(() => {
    if (i === index && !disabled) {
      setHeight(ref!.current!.scrollHeight);
    }
  }, [index, i]);

  return (
    <Wrapper style={style}>
      <LabelWrapper
        color={color}
        direction={direction}
        justify={justify}
        breaker={breaker}
        onClick={onClick}
      >
        {arrow && (
          <Icon color={color} iconColor={iconColor}>
            {open ? (
              <Arrow style={{ transition: 'all ease 0.25s' }} />
            ) : (
              <Arrow
                style={{
                  transform: 'rotate(180deg)',
                  transition: 'all ease 0.25s',
                }}
              />
            )}
          </Icon>
        )}
        {!arrow && (
          <Icon color={buttonColor || color} iconColor={iconColor}>
            {open ? '—' : '+'}
          </Icon>
        )}
        <Gap />
        <Label color={color} header={header}>
          {label}
        </Label>
      </LabelWrapper>
      {!disabled && (
        <Children
          color={color}
          iconColor={iconColor}
          style={{
            maxHeight: open ? height : 0,
            marginLeft: direction === 'row' ? 50 : 0,
          }}
          ref={ref}
        >
          <Padding>{children}</Padding>
          <Slip />
        </Children>
      )}
    </Wrapper>
  );
};

interface ILabelWrapper {
  direction: string;
  justify: string;
  breaker: boolean;
}
interface IColor {
  color: string;
  iconColor: string;
}
interface ILabel {
  header: boolean;
  color: string;
}

const Wrapper = styled.div`
  width: 100%;
`;

const LabelWrapper = styled.div<ILabelWrapper>`
  ${({ color, direction, justify, breaker }) => css`
    display: flex;
    align-items: center;
    flex-direction: ${direction};
    justify-content: ${justify};
    cursor: pointer;
    padding: 5px 0px;
    border-top: 1px solid ${breaker ? color : 'transparent'};
  `}
`;

const Icon = styled.div<IColor>`
  ${({ color, iconColor, theme: { Consts } }) => css`
    min-width: 26px;
    width: 26px;
    height: 18px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 10px;
    border-bottom-left-radius: 3px;
    background: ${color};
    color: ${iconColor};
    font-size: 12px;
    font-weight: bold;
    @media (min-width: ${Consts.tabletBreakPoint}) {
      min-width: 40px;
      width: 40px;
      height: 30px;
      font-size: 16px;
    }
  `}
`;

const Label = styled.div<ILabel>`
  ${({ header, color, theme: { Consts } }) => css`
    font-size: ${header ? `20px` : `14px`};
    font-weight: ${header ? `800` : `400`};
    color: ${color};
    @media (max-width: ${Consts.tabletBreakPoint}) {
      font-size: 15px;
    }
  `}
`;

const Children = styled.div<IColor>`
  ${({ color, theme: { Consts } }) => css`
    margin-left: 36px;
    color: ${color};
    overflow: hidden;
    font-size: 14px;
    transition: visibility ease 0.25s;
    transition: max-height ease 0.25s;
    @media (min-width: ${Consts.tabletBreakPoint}) {
      font-size: 18px;
    }

    a {
      text-decoration: underline;
      color: ${color};
    }
  `}
`;

const Slip = styled.div`
  width: 100%;
  height: 20px;
`;

const Gap = styled.div`
  width: 10px;
  height: 10px;
`;

const Padding = styled.div`
  width: 100%;
  padding: 10px 0px;
`;

//
